import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { runtimeConfig } from '../config/runtime';

/**
 * Base API service class that provides shared Axios configuration
 * and common functionality for all domain-specific services
 */
export class BaseApiService {
  protected api: AxiosInstance;

  constructor() {
    // Initialize with empty baseURL - will be set after runtime config is loaded
    this.api = axios.create({
      baseURL: '',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      config => {
        // Set base URL from runtime config if not already set
        if (!config.baseURL || config.baseURL === '') {
          config.baseURL = runtimeConfig.get('API_URL');
        }

        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Initialize the API service with runtime configuration
   */
  async initialize(): Promise<void> {
    await runtimeConfig.initialize();
    const apiUrl = runtimeConfig.get('API_URL');
    this.api.defaults.baseURL = apiUrl;
  }

  /**
   * Ensure the API service is initialized before making requests
   */
  protected async ensureInitialized(): Promise<void> {
    if (!runtimeConfig.isInitialized()) {
      await this.initialize();
    }
  }

  /**
   * Generic request method for custom API calls
   */
  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    await this.ensureInitialized();
    const response = await this.api.request<T>(config);
    return response.data;
  }

  /**
   * Set authentication token in headers
   */
  setAuthToken(token: string): void {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove authentication token from headers
   */
  removeAuthToken(): void {
    delete this.api.defaults.headers.common['Authorization'];
  }

  /**
   * Get authentication token from localStorage
   */
  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}
